/**
 * Free shipping bar (v2) — gift auto-add controller.
 *
 * Reads the JSON config emitted by layout/theme.liquid
 * (`#free-shipping-bar-config`: an array of { threshold, variantId, title }) and keeps each
 * configured "gift" tier in sync with the cart.
 *
 * Making the gift free is the merchant's automatic "Buy X, Get Y" discount; this script only
 * adds/removes the line (native Shopify BXGY never auto-adds the Get item). The gift line is tagged
 * with the line-item property `_free_gift: 'true'` so it can be identified and excluded from the
 * qualifying total.
 *
 * Behaviour (hysteresis / re-arm state machine), per gift tier:
 *   - Rising edge: when the qualifying total first reaches the threshold, auto-add the gift ONCE.
 *   - While still qualifying: the gift is a normal, removable line. If the customer removes it, it is
 *     NOT re-added (we respect the removal) — the tier stays "used" until re-armed.
 *   - Falling edge: if the total drops below the threshold, remove the gift (it would otherwise be
 *     charged full price, since the discount no longer applies) and RE-ARM the tier.
 *   - The next time the total crosses the threshold upward, the gift is auto-added again.
 *
 * A tier only qualifies while the cart holds at least one paid (non-gift) line, so a threshold-0
 * tier ("gift with every order") is added on the first add-to-cart and removed/re-armed when the
 * paid items are gone (a gift-only cart would otherwise be charged full price).
 *
 * The "used/armed" state is persisted per variant in localStorage so it survives page reloads and
 * section re-renders. It self-heals: any time a tier doesn't qualify (including an empty cart),
 * that tier is re-armed. A tier is only marked used once the add actually succeeded.
 *
 * All add-to-cart paths in this theme funnel through <product-form>, which dispatches `on:cart:add`
 * (new line) or `on:line-item:change` (quantity bump). Listening to both on `document` covers every
 * add-to-cart button (PDP, quick add, recommendations, promoted products).
 *
 * Degrades gracefully: any failure is swallowed and the server-rendered visual bar still works.
 */
(() => {
  if (window.__freeShippingBarInit) return;
  window.__freeShippingBarInit = true;

  const routes = (window.theme && window.theme.routes) || {};
  const CART_URL = routes.cart || '/cart';
  const CART_ADD = routes.cartAdd || '/cart/add';
  const CART_CHANGE = routes.cartChange || '/cart/change';
  const GIFT_PROP = '_free_gift';
  const ARMED_KEY = (variantId) => `fsb_used_${variantId}`; // '1' = already auto-added this qualification period

  let busy = false;
  let queued = false;

  const postJSON = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  /** @returns {Array<{threshold:number, variantId:number, title:string}>} */
  function readConfig() {
    const el = document.getElementById('free-shipping-bar-config');
    if (!el) return [];
    try {
      const parsed = JSON.parse((el.textContent || '').trim() || '[]');
      return Array.isArray(parsed) ? parsed.filter((t) => t && t.variantId && t.threshold >= 0) : [];
    } catch (e) {
      return [];
    }
  }

  const isGiftLine = (item) => {
    const p = item.properties || {};
    return p[GIFT_PROP] === 'true' || p[GIFT_PROP] === true;
  };

  // Per-tier "already auto-added this period" flag (the re-arm mechanism).
  const isUsed = (variantId) => {
    try {
      return localStorage.getItem(ARMED_KEY(variantId)) === '1';
    } catch (e) {
      return false;
    }
  };
  const setUsed = (variantId, val) => {
    try {
      if (val) localStorage.setItem(ARMED_KEY(variantId), '1');
      else localStorage.removeItem(ARMED_KEY(variantId));
    } catch (e) {
      /* storage unavailable — degrade to add-once-per-page behaviour */
    }
  };

  /** Re-render the visible cart UI (drawer and/or main cart page). */
  async function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

    const cartItemsEl = document.querySelector('.cc-main-cart cart-items, #cart cart-items');
    if (!cartItemsEl) return;

    const summaryEl = document.getElementById('cart-summary');
    const sectionNames = [
      ...new Set(
        ['free-shipping-notice', 'cart-icon-bubble', cartItemsEl.dataset.section, summaryEl && summaryEl.dataset.section].filter(
          Boolean
        )
      )
    ];

    try {
      const res = await fetch(`${window.location.pathname}?sections=${sectionNames.join(',')}`);
      const data = await res.json();
      const frag = (name) => {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = data[name] || '';
        return tmpl.content;
      };
      const replaceInner = (targetSel, sourceRoot, innerSel) => {
        const target = document.querySelector(targetSel);
        const source = sourceRoot.querySelector(innerSel);
        if (target && source) target.innerHTML = source.innerHTML;
      };

      if (data['free-shipping-notice'] != null) {
        replaceInner('#free-shipping-notice', frag('free-shipping-notice'), '.free-shipping-notice');
      }
      const mainSection = frag(cartItemsEl.dataset.section);
      replaceInner('.cc-main-cart cart-items, #cart cart-items', mainSection, 'cart-items');
      const summaryContent = mainSection.querySelector('.cart__summary');
      const summaryTarget = document.querySelector('.cart__summary');
      if (summaryContent && summaryTarget) summaryTarget.innerHTML = summaryContent.innerHTML;
    } catch (e) {
      /* server state is already correct; leave UI to next natural render */
    }
  }

  async function reconcile() {
    const config = readConfig();
    if (!config.length) return;
    if (busy) {
      queued = true;
      return;
    }
    busy = true;

    try {
      const cart = await (await fetch(`${CART_URL}.js`)).json();

      // Qualifying total excludes gift lines so the gift can't inflate progress.
      let qualifying = 0;
      let paidLines = 0;
      const giftLinesByVariant = {};
      (cart.items || []).forEach((item) => {
        if (isGiftLine(item)) {
          giftLinesByVariant[item.variant_id] = { key: item.key, quantity: item.quantity };
        } else {
          qualifying += item.final_line_price;
          paidLines += 1;
        }
      });

      const changes = []; // { id: lineKey, quantity: 0 }
      const adds = []; // { id: variantId, quantity, properties }

      config.forEach((tier) => {
        const qualifies = paidLines > 0 && qualifying >= tier.threshold;
        const inCart = Boolean(giftLinesByVariant[tier.variantId]);
        const used = isUsed(tier.variantId);

        if (qualifies) {
          if (!used) {
            // Rising edge — first qualify this period: auto-add once.
            if (inCart) {
              setUsed(tier.variantId, true);
            } else {
              adds.push({ id: tier.variantId, quantity: 1, properties: { [GIFT_PROP]: 'true' } });
            }
          }
          // else: already auto-added this period — respect a manual removal, do nothing.
        } else {
          // Below threshold: remove the (now full-price) gift if present, and re-arm the tier.
          if (inCart) {
            changes.push({ id: giftLinesByVariant[tier.variantId].key, quantity: 0 });
          }
          setUsed(tier.variantId, false);
        }
      });

      if (!changes.length && !adds.length) return;

      // Removals first (by line key), then adds.
      for (const ch of changes) {
        // eslint-disable-next-line no-await-in-loop
        await fetch(CART_CHANGE, postJSON(ch));
      }
      if (adds.length) {
        // Only mark tiers used once the add succeeded (e.g. a sold-out gift returns 422 — retry later).
        const res = await fetch(CART_ADD, postJSON({ items: adds }));
        if (res.ok) adds.forEach((a) => setUsed(a.id, true));
      }

      await refreshCartUI();
    } catch (e) {
      /* silent — informational bar still reflects server state on next render */
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        reconcile();
      }
    }
  }

  // Covers every add-to-cart button (all route through <product-form>), plus cart quantity edits.
  document.addEventListener('on:cart:add', reconcile);
  document.addEventListener('on:line-item:change', reconcile);
  document.addEventListener('DOMContentLoaded', reconcile);
  if (document.readyState !== 'loading') reconcile();
})();
